import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  PiggyBank,
  AlertTriangle,
  ArrowRight,
  Wallet,
  Target
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CentseScoreFinance from "./CentseScoreFinance";

/**
 * Basic Finance View Component
 * 
 * Simplified financial dashboard showing only the essentials:
 * - Account balance
 * - Monthly income vs outgoings
 * - Centse Score
 * - Critical alerts only
 */

// Mock financial data - replace with real data
const mockBasicFinanceData = {
  totalBalance: 2450.00,
  monthlyIncome: 3200.00,
  monthlyOutgoings: 2780.00,
  monthlySavings: 420.00,
  savingsGoal: 5000.00,
  currentSavings: 1850.00,
  criticalAlerts: [
    {
      id: 1,
      type: "budget_warning",
      title: "Dining Budget Alert",
      message: "85% of dining budget used with 8 days left",
      priority: "high"
    },
    {
      id: 2,
      type: "bill_due",
      title: "Credit Card Due",
      message: "Payment of $280 due in 3 days",
      priority: "high"
    }
  ]
};

const BasicFinanceView = () => {
  const navigate = useNavigate();
  const { 
    totalBalance, 
    monthlyIncome, 
    monthlyOutgoings, 
    monthlySavings,
    savingsGoal,
    currentSavings,
    criticalAlerts 
  } = mockBasicFinanceData;

  const savingsProgress = (currentSavings / savingsGoal) * 100;
  const monthlySavingsRate = (monthlySavings / monthlyIncome) * 100;

  return (
    <div className="space-y-6">
      {/* Centse Score */}
      <CentseScoreFinance />

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Balance */}
        <Card className="bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Balance</p>
                <p className="text-3xl font-bold text-gray-900">${totalBalance.toFixed(2)}</p>
                <p className="text-xs text-green-600 mt-1">+${monthlySavings.toFixed(2)} this month</p>
              </div>
              <div className="p-3 bg-secondary/20 rounded-full">
                <DollarSign className="h-6 w-6 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Income */}
        <Card className="bg-background border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Income</p>
                <p className="text-3xl font-bold text-gray-900">${monthlyIncome.toFixed(0)}</p>
                <p className="text-xs text-gray-500">After taxes</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Outgoings */}
        <Card className="bg-background border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Outgoings</p>
                <p className="text-3xl font-bold text-gray-900">${monthlyOutgoings.toFixed(0)}</p>
                <p className="text-xs text-gray-500">{monthlySavingsRate.toFixed(0)}% savings rate</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Savings Progress */}
      <Card className="bg-background border-gray-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <PiggyBank className="h-5 w-5" />
              Savings Goal Progress
            </CardTitle>
            <Badge variant="secondary" className="bg-secondary/10 text-secondary">
              {savingsProgress.toFixed(0)}% Complete
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm text-gray-600">
              <span>${currentSavings.toFixed(2)} saved</span>
              <span>${savingsGoal.toFixed(2)} goal</span>
            </div>
            <Progress value={savingsProgress} className="h-3" />
            <p className="text-xs text-gray-500 text-center">
              ${(savingsGoal - currentSavings).toFixed(2)} remaining to reach your goal
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Critical Alerts Only */}
      {criticalAlerts.length > 0 && (
        <Card className="bg-background border-red-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Critical Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {criticalAlerts.map((alert) => (
                <div 
                  key={alert.id}
                  className="p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">{alert.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    </div>
                    <Badge variant="destructive" className="text-xs">
                      {alert.priority}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card 
          className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
          onClick={() => navigate("/save")}
        >
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="p-3 bg-secondary/10 rounded-lg w-fit group-hover:scale-110 transition-transform">
                <PiggyBank className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Manage Savings</h3>
                <p className="text-sm text-gray-600">Set goals and track progress</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
          onClick={() => navigate("/invest")}
        >
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="p-3 bg-green-100 rounded-lg w-fit group-hover:scale-110 transition-transform">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Invest & Grow</h3>
                <p className="text-sm text-gray-600">Build your portfolio</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
          onClick={() => navigate("/track")}
        >
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="p-3 bg-blue-100 rounded-lg w-fit group-hover:scale-110 transition-transform">
                <Wallet className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Track Expenses</h3>
                <p className="text-sm text-gray-600">Monitor your spending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BasicFinanceView;